import { ActionIcon, Group, Stack, Text, Textarea } from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import { memo, useCallback } from 'react'

import { AISplitButton } from '@/components/AISplitButton'
import type { AIFieldState } from '@/hooks/types/aiFieldGeneration'
import * as aiInputStyles from '@/styles/AIInputWrapper.css'
import type { FAQItem } from '@/types/faq'

interface FAQEntryProps {
  faq: FAQItem
  onUpdate: (id: string, field: 'question' | 'answer', value: string) => void
  onDelete: (id: string) => void
  onGenerate: (id: string) => void
  onAccept: (id: string) => void
  onReject: (id: string) => void
  onRegenerate: (id: string) => void
  fieldState: AIFieldState
  disabled?: boolean
  showValidationErrors?: boolean
  isAnyOtherFAQGenerating?: boolean
}

export const FAQEntry = memo<FAQEntryProps>(function FAQEntry({
  faq,
  onUpdate,
  onDelete,
  onGenerate,
  onAccept,
  onReject,
  onRegenerate,
  fieldState,
  disabled = false,
  showValidationErrors = false,
  isAnyOtherFAQGenerating = false,
}) {
  const isPending = fieldState.state === 'pending'
  const isLoading = fieldState.state === 'loading'
  const isAIActive = isPending || isLoading

  // Parse suggestion if available
  let suggestedQuestion = faq.question
  let suggestedAnswer = faq.answer
  if (isPending && fieldState.suggestion) {
    try {
      const parsed = JSON.parse(fieldState.suggestion) as { question: string; answer: string }
      suggestedQuestion = parsed.question
      suggestedAnswer = parsed.answer
    } catch (error) {
      console.error('Failed to parse FAQ suggestion:', error)
    }
  }

  const handleAccept = useCallback(() => {
    onAccept(faq.id)
  }, [faq.id, onAccept])

  const handleReject = useCallback(() => {
    onReject(faq.id)
  }, [faq.id, onReject])

  const handleQuestionChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onUpdate(faq.id, 'question', event.target.value)
    },
    [faq.id, onUpdate],
  )

  const handleAnswerChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      onUpdate(faq.id, 'answer', event.target.value)
    },
    [faq.id, onUpdate],
  )

  const handleDelete = useCallback(() => {
    onDelete(faq.id)
  }, [faq.id, onDelete])

  const handleGenerate = useCallback(() => {
    onGenerate(faq.id)
  }, [faq.id, onGenerate])

  const handleRegenerate = useCallback(() => {
    onRegenerate(faq.id)
  }, [faq.id, onRegenerate])

  const questionHasError = showValidationErrors && faq.question.trim().length === 0
  const answerHasError = showValidationErrors && faq.answer.trim().length === 0
  const hasError = questionHasError || answerHasError
  const isIncomplete = hasError

  return (
    <Stack
      gap="xs"
      p="md"
      style={{
        border: `1px solid ${hasError ? 'var(--mantine-color-red-4)' : 'var(--mantine-color-gray-3)'}`,
        borderRadius: '8px',
        opacity: disabled ? 0.6 : 1,
        backgroundColor: hasError ? 'var(--mantine-color-red-0)' : undefined,
      }}>
      <Group justify="flex-end" align="center">
        <AISplitButton
          fieldState={fieldState.state}
          onAskSidekick={handleGenerate}
          onAccept={handleAccept}
          onReject={handleReject}
          onRegenerate={handleRegenerate}
          disabled={disabled || isAnyOtherFAQGenerating}
        />
      </Group>

      <Group justify="space-between" align="flex-start">
        <Stack gap="xs" style={{ flex: 1 }}>
          <Textarea
            placeholder="Enter your question..."
            value={isPending ? suggestedQuestion : faq.question}
            onChange={e => {
              if (isPending) {
                handleAccept()
              }
              handleQuestionChange(e)
            }}
            autosize
            minRows={1}
            maxRows={3}
            size="sm"
            disabled={disabled || isLoading}
            error={questionHasError}
            variant={isAIActive ? 'filled' : undefined}
            classNames={{
              input: isAIActive ? aiInputStyles.input : undefined,
            }}
            styles={{
              input: isAIActive
                ? undefined
                : {
                    fontWeight: 500,
                    borderColor: questionHasError ? 'var(--mantine-color-red-4)' : undefined,
                  },
            }}
          />
          <Textarea
            placeholder="Enter the answer..."
            value={isPending ? suggestedAnswer : faq.answer}
            onChange={e => {
              if (isPending) {
                handleAccept()
              }
              handleAnswerChange(e)
            }}
            autosize
            minRows={2}
            maxRows={6}
            size="sm"
            disabled={disabled || isLoading}
            error={answerHasError}
            variant={isAIActive ? 'filled' : undefined}
            classNames={{
              input: isAIActive ? aiInputStyles.input : undefined,
            }}
            styles={{
              input: isAIActive
                ? undefined
                : {
                    borderColor: answerHasError ? 'var(--mantine-color-red-4)' : undefined,
                  },
            }}
          />
          {/* Show validation message */}
          {isIncomplete && (
            <Text size="xs" c="orange" fw={500}>
              Both question and answer are required to save this FAQ
            </Text>
          )}
        </Stack>

        <ActionIcon variant="subtle" color="red" size="sm" onClick={handleDelete} disabled={disabled || isLoading}>
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
    </Stack>
  )
})
